import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiEye, FiSearch } from 'react-icons/fi';
import { getOrder, getOrders, updateOrderStatus } from '../services/api';

const statusOptions = ['placed', 'approved', 'processing', 'dispatched', 'delivered', 'cancelled', 'rejected'];

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUserType, setFilterUserType] = useState('');
  const [search, setSearch] = useState('');
  const [showDetail, setShowDetail] = useState<any>(null);
  const [statusForm, setStatusForm] = useState({ status: '', trackingNumber: '', adminRemarks: '' });

  const loadOrders = useCallback(async (searchValue = search) => {
    try {
      setLoading(true);
      const res = await getOrders({
        page,
        pageSize: 20,
        status: filterStatus,
        userType: filterUserType,
        search: searchValue,
      });
      setOrders(res.data.data ?? []);
      setTotal(res.data.totalCount ?? 0);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load redeem requests');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterUserType, page, search]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const viewOrder = async (id: number) => {
    try {
      const res = await getOrder(id);
      const data = res.data.data;
      setShowDetail(data);
      setStatusForm({
        status: data.status,
        trackingNumber: data.trackingNumber || '',
        adminRemarks: data.adminRemarks || '',
      });
    } catch (err) {
      console.error(err);
      toast.error('Unable to load request details');
    }
  };

  const handleStatusUpdate = async () => {
    try {
      await updateOrderStatus(showDetail.orderId, statusForm);
      toast.success('Redeem request updated');
      setShowDetail(null);
      loadOrders();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update request');
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadOrders(search);
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      placed: 'badge-warning',
      approved: 'badge-info',
      processing: 'badge-info',
      dispatched: 'badge-secondary',
      delivered: 'badge-success',
      cancelled: 'badge-danger',
      rejected: 'badge-danger',
    };
    return map[status] || 'badge-secondary';
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Product Redeem Requests</h1>
        <p>Review orders placed from schemes, approve them, and update dispatch details.</p>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="search-filters">
            <div className="search-box">
              <FiSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search by order number, customer or mobile..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <select
              className="filter-dropdown"
              value={filterUserType}
              onChange={(e) => {
                setFilterUserType(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Types</option>
              <option value="dealer">Dealer</option>
              <option value="contractor">Contractor</option>
            </select>
            <select
              className="filter-dropdown"
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Statuses</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
            <button className="btn btn-outline" onClick={handleSearch}>
              <FiSearch /> Search
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading redeem requests...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Request</th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Points</th>
                  <th>Status</th>
                  <th>Shipping City</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.orderId}>
                    <td>
                      <strong>{order.orderNumber}</strong>
                    </td>
                    <td>
                      <div className="stack-sm">
                        <span>{order.fullName}</span>
                        <span className="text-muted">{order.mobile}</span>
                      </div>
                    </td>
                    <td>
                      <span className="badge badge-info">{order.userType}</span>
                    </td>
                    <td>{order.totalPoints.toLocaleString('en-IN')} pts</td>
                    <td>
                      <span className={`badge ${getStatusBadge(order.status)}`}>{order.status}</span>
                    </td>
                    <td>{order.shippingCity || '-'}</td>
                    <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button className="btn btn-sm btn-outline" onClick={() => viewOrder(order.orderId)}>
                        <FiEye /> View
                      </button>
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center">No redeem requests found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>

      {showDetail && (
        <div className="modal-overlay" onClick={() => setShowDetail(null)}>
          <div className="modal" style={{ minWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Redeem Request: {showDetail.orderNumber}</h3>
              <button className="modal-close" onClick={() => setShowDetail(null)}>&times;</button>
            </div>

            <div className="details-grid">
              <div className="detail-item">
                <label>Customer</label>
                <span>{showDetail.userName}</span>
              </div>
              <div className="detail-item">
                <label>Mobile</label>
                <span>{showDetail.userMobile}</span>
              </div>
              <div className="detail-item">
                <label>User Type</label>
                <span>{showDetail.userType}</span>
              </div>
              <div className="detail-item">
                <label>Total Points</label>
                <span>{showDetail.totalPoints} pts</span>
              </div>
            </div>

            <h4 className="mb-10 mt-20">Requested Products</h4>
            <div className="table-container mb-20">
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Points/Item</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {showDetail.items?.map((item: any, index: number) => (
                    <tr key={index}>
                      <td>{item.itemName}</td>
                      <td>{item.quantity}</td>
                      <td>{item.pointsPerItem}</td>
                      <td>{item.totalPoints}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h4 className="mb-10">Shipping Details</h4>
            <div className="details-grid">
              <div className="detail-item">
                <label>Name</label>
                <span>{showDetail.shippingName || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Mobile</label>
                <span>{showDetail.shippingMobile || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Address</label>
                <span>{showDetail.shippingAddress || '-'}</span>
              </div>
              <div className="detail-item">
                <label>City / State / PIN</label>
                <span>
                  {[showDetail.shippingCity, showDetail.shippingState, showDetail.shippingPinCode]
                    .filter(Boolean)
                    .join(', ') || '-'}
                </span>
              </div>
            </div>

            <h4 className="mb-10 mt-20">Update Request Status</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Status</label>
                <select
                  className="form-control"
                  value={statusForm.status}
                  onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Tracking Number</label>
                <input
                  className="form-control"
                  value={statusForm.trackingNumber}
                  onChange={(e) => setStatusForm({ ...statusForm, trackingNumber: e.target.value })}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Admin Remarks</label>
              <textarea
                className="form-control"
                rows={3}
                value={statusForm.adminRemarks}
                onChange={(e) => setStatusForm({ ...statusForm, adminRemarks: e.target.value })}
              />
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowDetail(null)}>Close</button>
              <button className="btn btn-primary" onClick={handleStatusUpdate}>Update Status</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
